// Requêtes des écrans du budget et de la chaîne de la dépense (lot L4-B, TanStack Query). Clés sous
// « budget » et « depense » ; le changement de contexte vide le cache (FournisseurSession). Les types
// viennent du contrat commun (packages/contrats/src/budget.ts et depense.ts).
import type {
  BesoinDepense,
  Budget,
  CreationBesoin,
  CreationBudget,
  CreationEngagement,
  CreationLigneBudget,
  CreationLiquidation,
  DecisionModificative,
  EngagementDepense,
  EtatBudgetaire,
  LigneBudgetaire,
  LiquidationDepense,
  MandatDepense,
  MouvementCredit,
  PaiementConfirme,
  SaisieServiceFait,
  SectionBudget,
  TransitionBudget,
} from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { telechargerBlob } from '../metier/csv';

export interface Liste<T> {
  elements: T[];
}

export interface DecisionModificativeGroupee {
  reference: string;
  date: string;
  deliberation_reference: string | null;
  motif: string;
  total: number;
  mouvements: MouvementCredit[];
}

export const INVALIDER_BUDGET: readonly (readonly unknown[])[] = [['budget'], ['depense'], ['indicateurs'], ['decisions']];

const avecSignal = <T,>(chemin: string, parametres: Record<string, string | undefined> = {}) => ({ signal }: { signal: AbortSignal }) => requete<T>(chemin, { signal, parametres });

export function useBudgets() {
  return useQuery({ queryKey: ['budget', 'liste'], queryFn: avecSignal<Liste<Budget>>('/budgets'), retry: false });
}

export function useBudget(id: string | null) {
  return useQuery({ queryKey: ['budget', 'fiche', id ?? ''], queryFn: avecSignal<Budget>(`/budgets/${id}`), enabled: Boolean(id), retry: false });
}

export function useLignes(id: string | null, section?: SectionBudget) {
  return useQuery({ queryKey: ['budget', 'lignes', id ?? '', section ?? ''], queryFn: avecSignal<Liste<LigneBudgetaire>>(`/budgets/${id}/lignes`, { section }), enabled: Boolean(id), retry: false });
}

export function useDecisionsModificatives(id: string | null) {
  return useQuery({ queryKey: ['budget', 'decisions', id ?? ''], queryFn: avecSignal<Liste<DecisionModificativeGroupee>>(`/budgets/${id}/decisions`), enabled: Boolean(id), retry: false });
}

export function useBesoins(etat?: string) {
  return useQuery({ queryKey: ['depense', 'besoins', etat ?? ''], queryFn: avecSignal<Liste<BesoinDepense>>('/depense/besoins', { etat }), retry: false });
}

export function useEngagements(budgetId: string | null) {
  return useQuery({ queryKey: ['depense', 'engagements', budgetId ?? ''], queryFn: avecSignal<Liste<EngagementDepense>>('/depense/engagements', { budget_id: budgetId ?? undefined }), retry: false });
}

export function useLiquidations(budgetId: string | null) {
  return useQuery({ queryKey: ['depense', 'liquidations', budgetId ?? ''], queryFn: avecSignal<Liste<LiquidationDepense>>('/depense/liquidations', { budget_id: budgetId ?? undefined }), retry: false });
}

export function useMandats(budgetId: string | null) {
  return useQuery({ queryKey: ['depense', 'mandats', budgetId ?? ''], queryFn: avecSignal<Liste<MandatDepense>>('/depense/mandats', { budget_id: budgetId ?? undefined }), retry: false });
}

export interface ServiceSimple {
  id: string;
  nom: string;
}

export function useServices() {
  return useQuery({ queryKey: ['budget', 'services'], queryFn: avecSignal<Liste<ServiceSimple>>('/services'), staleTime: 60_000, retry: false });
}

// Écritures ----------------------------------------------------------------------------------

const poster = <T,>(chemin: string, corps: unknown) => requete<T>(chemin, { methode: 'POST', corps });

export const creerBudget = (corps: CreationBudget) => poster<Budget>('/budgets', corps);
export const transiterBudget = (id: string, corps: TransitionBudget) => poster<Budget>(`/budgets/${id}/transitions`, corps);
export const creerLigne = (id: string, corps: CreationLigneBudget) => poster<LigneBudgetaire>(`/budgets/${id}/lignes`, corps);
export const importerNomenclature = (id: string, csv: string) => poster<{ importees: number; total_prevu: number }>(`/budgets/${id}/import`, { csv });
export const deciderModification = (id: string, corps: DecisionModificative) => poster<DecisionModificativeGroupee>(`/budgets/${id}/decisions`, corps);

export const creerBesoin = (corps: CreationBesoin) => poster<BesoinDepense>('/depense/besoins', corps);
export const validerBesoin = (id: string) => poster<BesoinDepense>(`/depense/besoins/${id}/valider`, {});
export const refuserBesoin = (id: string, motif: string) => poster<BesoinDepense>(`/depense/besoins/${id}/refuser`, { motif });
export const engager = (corps: CreationEngagement) => poster<EngagementDepense>('/depense/engagements', corps);
export const deroger = (id: string, motif: string) => poster<EngagementDepense>(`/depense/engagements/${id}/derogation`, { motif });
export const constaterServiceFait = (id: string, corps: SaisieServiceFait) => poster<EngagementDepense>(`/depense/engagements/${id}/service-fait`, corps);
export const liquider = (corps: CreationLiquidation) => poster<LiquidationDepense>('/depense/liquidations', corps);
export const ordonnancer = (liquidationId: string) => poster<MandatDepense>('/depense/mandats', { liquidation_id: liquidationId });
export const confirmerPaiement = (corps: PaiementConfirme) => poster<MandatDepense>('/depense/paiements', corps);

/** Télécharge un état budgétaire (balance ou journal des mandats) en CSV ou XLSX. */
export async function telechargerEtat(id: string, etat: EtatBudgetaire, format: 'csv' | 'xlsx', section?: SectionBudget): Promise<void> {
  const r = await requete<Response>(`/budgets/${id}/export`, { parametres: { etat, format, section }, brut: true });
  const disposition = r.headers.get('content-disposition') ?? '';
  const nom = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `${etat}.${format}`;
  telechargerBlob(nom, await r.blob());
}
